import type { User, Stream } from '$lib/shared/lib/types';
import type { UserNavigationState } from './types';

export const UserNavigationStates = {
	UNAUTHENTICATED: 'UNAUTHENTICATED',
	BANNED: 'BANNED',
	SUPERUSER: 'SUPERUSER',
	NEW_USER: 'NEW_USER',
	PENDING_APPROVAL: 'PENDING_APPROVAL',
	REJECTED: 'REJECTED',
	INACTIVE_USER: 'INACTIVE_USER',
	ACTIVE_USER: 'ACTIVE_USER',
	VIEWER: 'VIEWER'
} as const;

export function getNavigationState(user: User | null, streams?: Stream[]): UserNavigationState {
	if (!user) {
		return UserNavigationStates.UNAUTHENTICATED;
	}

	if (user.banned) {
		return UserNavigationStates.BANNED;
	}

	if (user.superuser) {
		return UserNavigationStates.SUPERUSER;
	}

	const userStreams = streams ?? user.streams;

	const isNewUser = userStreams.length === 0;

	if (isNewUser) {
		return UserNavigationStates.NEW_USER;
	}

	const hasVerifiedStream = userStreams.some((s) => s.stream_state === 'VERIFIED');
	const hasPendingStream = userStreams.some((s) => s.stream_state === 'NONE');
	const hasRejectedStream = userStreams.some((s) => s.stream_state === 'REJECTED');

	if (hasVerifiedStream) {
		const verifiedStreams = userStreams.filter((s) => s.stream_state === 'VERIFIED');

		const hasStripeEnabled = verifiedStreams.some((s) => s.stripe_connect_state === 'ENABLED');

		const hasActiveStream = verifiedStreams.some((s) => s.stripe_connect_state === 'ENABLED');

		if (hasStripeEnabled && hasActiveStream) {
			return UserNavigationStates.ACTIVE_USER;
		}

		return UserNavigationStates.INACTIVE_USER;
	}

	if (hasPendingStream) {
		return UserNavigationStates.PENDING_APPROVAL;
	}

	if (hasRejectedStream) {
		return UserNavigationStates.REJECTED;
	}

	return UserNavigationStates.VIEWER;
}

export function getDefaultRoute(state: UserNavigationState): string {
	switch (state) {
		case UserNavigationStates.UNAUTHENTICATED:
			return '/signin';
		case UserNavigationStates.BANNED:
			return '/';
		case UserNavigationStates.NEW_USER:
		case UserNavigationStates.PENDING_APPROVAL:
		case UserNavigationStates.REJECTED:
			return '/onboard';
		case UserNavigationStates.INACTIVE_USER:
		case UserNavigationStates.ACTIVE_USER:
		case UserNavigationStates.SUPERUSER:
			return '/admin';
		case UserNavigationStates.VIEWER:
			return '/';
		default:
			return '/';
	}
}

export function getAllowedRoutes(state: UserNavigationState): string[] {
	const publicRoutes = ['/', '/signin', '/privacy', '/terms', '/[tag]'];

	const commonAuthenticatedRoutes = ['/account'];

	switch (state) {
		case UserNavigationStates.UNAUTHENTICATED:
			//return [...publicRoutes, ''];
			return publicRoutes;

		case UserNavigationStates.BANNED:
			return publicRoutes;

		case UserNavigationStates.NEW_USER:
		case UserNavigationStates.PENDING_APPROVAL:
		case UserNavigationStates.REJECTED:
			return [...publicRoutes, ...commonAuthenticatedRoutes, '/onboard'];

		case UserNavigationStates.INACTIVE_USER:
		case UserNavigationStates.ACTIVE_USER:
			return [...publicRoutes, ...commonAuthenticatedRoutes, '/admin', '/admin/[tag]'];

		case UserNavigationStates.SUPERUSER:
			return [
				...publicRoutes,
				...commonAuthenticatedRoutes,
				'/admin',
				'/admin/[tag]',
				'/admin/streams',
				'/admin/speakers'
			];

		case UserNavigationStates.VIEWER:
			return [...publicRoutes, ...commonAuthenticatedRoutes];

		default:
			return publicRoutes;
	}
}

export function getOnboardingRedirect(state: UserNavigationState): string | null {
	switch (state) {
		case UserNavigationStates.NEW_USER:
		case UserNavigationStates.PENDING_APPROVAL:
		case UserNavigationStates.REJECTED:
			return '/onboard';
		default:
			return null;
	}
}

export function canAccessAdminPanel(state: UserNavigationState): boolean {
	return (
		state === UserNavigationStates.ACTIVE_USER ||
		state === UserNavigationStates.SUPERUSER ||
		state === UserNavigationStates.INACTIVE_USER
	);
}

export function canAccessSuperuserRoutes(state: UserNavigationState): boolean {
	return state === UserNavigationStates.SUPERUSER;
}

export function isOnboardingIncomplete(state: UserNavigationState): boolean {
	return (
		[
			UserNavigationStates.NEW_USER,
			UserNavigationStates.PENDING_APPROVAL,
			UserNavigationStates.REJECTED
		] as UserNavigationState[]
	).includes(state);
}
